import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Crown,
  PaperPlaneTilt,
  CircleNotch,
  CheckCircle,
  Warning,
  Prohibit,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * FoundingProReviewTile — admin runner for the comped-Pro completeness
 * campaign. Three actions:
 *
 *   1. Send "Initial" warning email (Aug 1 deadline) to any comped
 *      Pro missing logo / cover / description / 2+ photos / website-or-menu link
 *   2. Send "Final 7-Day Reminder" email — fires ~July 25 for any
 *      truck still incomplete after the initial warning
 *   3. Revoke Pro per-truck once the deadline passes
 *
 * Paying Pros (Stripe-subscribed, including the 6 founding $5/mo)
 * are excluded by the audience scan — the revoke endpoint also
 * refuses to revoke any paying Pro as a hard safety check.
 */
export default function FoundingProReviewTile() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [onlyEmail, setOnlyEmail] = useState("");
  const [mode, setMode] = useState("initial");
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const pollRef = useRef(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.get("/admin/maintenance/founding-pro-review/preview");
      setPreview(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Poll the running job every 2s
  useEffect(() => {
    if (!jobId) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/admin/maintenance/founding-pro-review/blast/${jobId}`);
        if (cancelled) return;
        setJobStatus(data);
        if (data.status === "done" || data.status === "error") {
          if (pollRef.current) clearInterval(pollRef.current);
          if (data.status === "done") {
            toast.success(`Done — sent ${data.sent}, failed ${data.failed}.`);
          } else {
            toast.error(`Errored: ${data.error || "unknown"}`);
          }
          await load();
        }
      } catch {
        /* swallow transient errors */
      }
    };
    tick();
    pollRef.current = setInterval(tick, 2000);
    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [jobId]);

  const startBlast = async (only) => {
    const modeLabel = mode === "final" ? "Final 7-Day Reminder" : "Initial Warning";
    const audienceLabel = only
      ? `Smoke test to ${only}`
      : `Send the ${modeLabel} to ALL ${preview?.incomplete?.length || 0} incomplete Founding Pros`;
    if (!window.confirm(audienceLabel + "?")) return;
    setJobStatus(null);
    try {
      const { data } = await api.post("/admin/maintenance/founding-pro-review/blast", {
        confirm: true, mode, only: only || null,
      });
      setJobId(data.job_id);
      const synthMsg = data.synthetic_preview ? " (preview render)" : "";
      toast.message(`Queued — ${data.audience_size} email${data.audience_size === 1 ? "" : "s"}${synthMsg}.`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const revoke = async (row) => {
    if (!window.confirm(
      `Revoke complimentary Pro for "${row.truck_name || row.email}"? This sets is_pro=false and stamps the revoke audit fields. Re-grant via the user-admin tools if needed.`,
    )) return;
    try {
      const { data } = await api.post("/admin/maintenance/founding-pro-review/revoke", {
        confirm: true, user_id: row.id,
      });
      if (data.revoked) {
        toast.success(`Revoked Pro for ${data.email}`);
      } else if (data.already_revoked) {
        toast.message(`${data.email} was already Free.`);
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const totalForJob = jobStatus?.total || 0;
  const doneForJob = (jobStatus?.sent || 0) + (jobStatus?.failed || 0);
  const pct = totalForJob ? Math.round((doneForJob / totalForJob) * 100) : 0;
  const isRunning = jobStatus && (jobStatus.status === "running" || jobStatus.status === "queued");

  return (
    <div data-testid="admin-founding-pro-review">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-base font-semibold flex items-center gap-2">
            <Crown weight="duotone" className="h-5 w-5 text-primary" />
            Founding Pro Review
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Comped Pros missing profile criteria. Paying Pros are auto-excluded.
            Deadline: <strong>{preview?.deadline || "August 1st"}</strong>.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="admin-founding-refresh">
          {loading ? <CircleNotch className="h-3 w-3 animate-spin" /> : "Refresh"}
        </Button>
      </div>

      {err && <p className="text-sm text-rose-600 py-2">{err}</p>}

      {preview && (
        <>
          <div className="grid grid-cols-3 gap-3 my-3 text-center">
            <div className="rounded-lg bg-muted/30 p-3">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Comped Pros</div>
              <div className="text-2xl font-bold" data-testid="admin-founding-total">{preview.total_comped}</div>
            </div>
            <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-900/40 p-3">
              <div className="text-xs text-emerald-700 dark:text-emerald-300 uppercase tracking-wide">Complete</div>
              <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-200" data-testid="admin-founding-complete">
                {preview.complete?.length || 0}
              </div>
            </div>
            <div className="rounded-lg bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-900/40 p-3">
              <div className="text-xs text-rose-700 dark:text-rose-300 uppercase tracking-wide">Incomplete</div>
              <div className="text-2xl font-bold text-rose-700 dark:text-rose-200" data-testid="admin-founding-incomplete">
                {preview.incomplete?.length || 0}
              </div>
            </div>
          </div>

          <div className="rounded-md border border-border bg-muted/30 p-3 text-xs">
            <div className="font-semibold uppercase tracking-wide text-muted-foreground mb-1">Criteria</div>
            <div>{preview.criteria}</div>
          </div>

          {/* Mode selector */}
          <div className="mt-4 rounded-md border border-border bg-card p-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Email mode
            </div>
            <div className="flex gap-2">
              {[
                { key: "initial", label: "Initial Warning", hint: "Aug 1 deadline" },
                { key: "final", label: "7-Day Final", hint: "Send ~July 25" },
              ].map((m) => (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => setMode(m.key)}
                  disabled={isRunning}
                  className={`flex-1 rounded-md border-2 px-3 py-2 text-left transition ${
                    mode === m.key
                      ? "border-primary bg-primary/5"
                      : "border-border bg-card hover:border-primary/40"
                  }`}
                  data-testid={`admin-founding-mode-${m.key}`}
                >
                  <div className="text-sm font-semibold">{m.label}</div>
                  <div className="text-[11px] text-muted-foreground">{m.hint}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Smoke test */}
          <div className="rounded-md border border-border bg-muted/20 p-3 my-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Smoke test
            </div>
            <div className="flex gap-2">
              <Input
                type="email" placeholder="your@email.com" value={onlyEmail}
                onChange={(e) => setOnlyEmail(e.target.value)}
                disabled={isRunning}
                data-testid="admin-founding-only-input"
              />
              <Button
                variant="outline"
                onClick={() => startBlast(onlyEmail.trim())}
                disabled={!onlyEmail.trim() || isRunning}
                data-testid="admin-founding-only-btn"
              >
                Send test
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1.5">
              Any real user email works. Renders a preview with all missing fields if you aren&apos;t in the live audience.
            </p>
          </div>

          {/* Send all */}
          <div className="flex justify-end">
            <Button
              onClick={() => startBlast(null)}
              disabled={isRunning || !(preview.incomplete?.length)}
              data-testid="admin-founding-blast-run"
            >
              {isRunning ? (
                <><CircleNotch className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
              ) : (
                <>Send {mode === "final" ? "7-Day Final" : "Initial Warning"} to {preview.incomplete?.length || 0}</>
              )}
            </Button>
          </div>

          {/* Live job status */}
          {jobStatus && (
            <div className="mt-4 rounded-md border border-border bg-card p-3" data-testid="admin-founding-status">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase font-semibold tracking-wide text-muted-foreground">
                  Job status · {jobStatus.mode || "initial"}
                </span>
                <StatusPill status={jobStatus.status} />
              </div>
              <div className="text-sm font-mono mb-2">
                {jobStatus.sent}/{totalForJob} sent · {jobStatus.failed} failed
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
              </div>
              {jobStatus.error && (
                <p className="text-xs text-rose-600 mt-2 break-all">Error: {jobStatus.error}</p>
              )}
            </div>
          )}

          {/* Audience list */}
          {preview.incomplete?.length > 0 && (
            <details className="mt-4 rounded-md border border-border bg-card" open data-testid="admin-founding-audience-details">
              <summary className="cursor-pointer px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:bg-muted/30">
                Incomplete audience · {preview.incomplete.length}
              </summary>
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-muted/50 backdrop-blur z-10">
                    <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                      <th className="px-3 py-2">Truck</th>
                      <th className="px-3 py-2">Missing</th>
                      <th className="px-3 py-2">Warned</th>
                      <th className="px-3 py-2 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.incomplete.map((row) => (
                      <tr key={row.id} className="border-t border-border/40" data-testid={`admin-founding-row-${row.id}`}>
                        <td className="px-3 py-2">
                          <div className="font-semibold truncate max-w-[200px]">
                            {row.truck_name || "(no truck name)"}
                          </div>
                          <div className="text-[10px] text-muted-foreground font-mono truncate max-w-[200px]">
                            {row.email}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <span className="text-rose-700 dark:text-rose-300">
                            {(row.missing || []).join(", ")}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-[10px] text-muted-foreground">
                          {row.warning_sent_at ? "Initial" : "—"}
                          {row.final_warning_sent_at ? " + Final" : ""}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => revoke(row)}
                            data-testid={`admin-founding-revoke-${row.id}`}
                          >
                            <Prohibit className="h-3 w-3 mr-1" /> Revoke
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          {preview.complete?.length > 0 && (
            <details className="mt-2 rounded-md border border-border bg-card" data-testid="admin-founding-complete-list">
              <summary className="cursor-pointer px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:bg-muted/30">
                Complete audience · {preview.complete.length}
              </summary>
              <div className="max-h-72 overflow-y-auto px-3 py-2">
                {preview.complete.map((row) => (
                  <div key={row.id} className="flex items-center gap-2 py-1 text-xs">
                    <CheckCircle weight="fill" className="h-3 w-3 text-emerald-500 flex-none" />
                    <span className="font-semibold truncate flex-1">{row.truck_name || row.email}</span>
                  </div>
                ))}
              </div>
            </details>
          )}

          {(preview.incomplete?.length === 0) && (
            <p className="mt-3 text-sm text-emerald-700 flex items-center gap-1.5">
              <CheckCircle weight="fill" className="h-4 w-4" />
              All comped Pros have complete profiles — no action needed.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const cls = {
    queued: "bg-amber-100 text-amber-700",
    running: "bg-blue-100 text-blue-700",
    done: "bg-emerald-100 text-emerald-700",
    error: "bg-rose-100 text-rose-700",
  }[status] || "bg-muted text-muted-foreground";
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${cls}`}>
      {status}
      {!status && <Warning className="h-3 w-3 inline ml-1" />}
    </span>
  );
}
